import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';

interface FooterColumn {
  id: string;
  heading: string;
  links: { label: string; href: string }[];
}

const sitemapColumns: FooterColumn[] = [
  {
    id: 'work-with-us',
    heading: 'Work With Us',
    links: [
      { label: 'Book a Sprint', href: '/book' },
      { label: 'Services', href: '/services' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Work With Daniel', href: '/work-with-daniel' },
      { label: 'Client Portal', href: '/login' },
    ],
  },
  {
    id: 'proof',
    heading: 'Proof',
    links: [
      { label: 'Portfolio', href: '/portfolio' },
      { label: 'Testimonials', href: '/testimonials' },
      { label: 'Case Studies', href: '/case-studies' },
    ],
  },
  {
    id: 'resources',
    heading: 'Resources',
    links: [
      { label: 'DIY Resources', href: '/diy' },
      { label: 'Insights', href: '/insights' },
      { label: 'Free NDA Template', href: '/free-nda' },
      { label: 'Tools', href: '/tools' },
    ],
  },
  {
    id: 'company',
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Team', href: '/team' },
      { label: 'Contact', href: '/contact' },
    ],
  },
];

const legalLinks = [
  { label: 'Terms', href: '/legal/terms' },
  { label: 'Privacy', href: '/legal/privacy' },
  { label: 'Cookies', href: '/legal/cookies' },
  { label: 'Refund', href: '/legal/refund' },
  { label: 'Trademarks', href: '/legal/trademarks' },
  { label: 'DMCA', href: '/legal/dmca' },
  { label: 'Data Retention', href: '/legal/data-retention' },
];

const socialLinks = [
  {
    id: 'social_facebook',
    href: 'https://www.facebook.com/bestoddscorp',
    label: 'Facebook',
    hoverColor: 'hover:text-[#1877F2]',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    id: 'social_twitter',
    href: 'https://twitter.com/bestoddscorp',
    label: 'Twitter',
    hoverColor: 'hover:text-gray-400',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    id: 'social_linkedin',
    href: 'https://www.linkedin.com/company/best-odds-corp',
    label: 'LinkedIn',
    hoverColor: 'hover:text-[#0A66C2]',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
      </svg>
    ),
  },
];

export default function Footer() {
  return (
    <footer className="bg-background border-t border-border pt-14 pb-10">
      <div className="max-w-[1600px] mx-auto px-6">
        {/* Main sitemap grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          {sitemapColumns.map((col) => (
            <div key={col.id}>
              <h3 className="text-[9px] uppercase tracking-ultra-wide text-foreground mb-5 font-semibold">
                {col.heading}
              </h3>
              <div className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <Link
                    key={link.href + link.label}
                    href={link.href}
                    className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Legal + Contact row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10 pt-8 border-t border-border">
          {/* Legal */}
          <div>
            <h3 className="text-[9px] uppercase tracking-ultra-wide text-foreground mb-5 font-semibold">Legal</h3>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[9px] uppercase tracking-ultra-wide text-foreground mb-5 font-semibold">Contact</h3>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icon name="MapPinIcon" size={14} className="text-primary flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Las Vegas, NV</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="EnvelopeIcon" size={14} className="text-primary flex-shrink-0" />
                <a href="mailto:hello@bestoddscorp.com" className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                  hello@bestoddscorp.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="PhoneIcon" size={14} className="text-primary flex-shrink-0" />
                <a href="tel:+17025466337" className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                  (702) 546-6337
                </a>
              </div>
              <div className="mt-1">
                <Badge variant="success">US-Based</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-5 pt-6 border-t border-border">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-[9px] uppercase tracking-ultra-wide text-muted-foreground">
              © 2026 Best Odds Corp. Est. 2000. All Rights Reserved.
            </p>
            <span className="hidden sm:block text-border">|</span>
            <Link href="/book" className="text-[9px] uppercase tracking-widest text-primary hover:text-primary/80 transition-colors font-semibold">
              Book a Sprint →
            </Link>
          </div>

          {/* Social Icons */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => (
              <a
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-9 h-9 border border-border flex items-center justify-center text-gray-300 hover:border-primary ${social.hoverColor} transition-all duration-200`}
                aria-label={social.label}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}